import React, { useId, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { ASSET_LIMITS } from '../config/uploadConfig';
import { pluralize } from '../utils/format';

export interface AssetEffect {
  id: string;
  type: string;
  intensity?: number;
  duration?: number;
  notes?: string;
}

interface AssetEffectsManagerProps {
  assetId: string;
  assetType: 'image' | 'audio';
  assetName: string;
  effects: AssetEffect[];
  onEffectsChange: (effects: AssetEffect[]) => void;
}

// These strings are stored on the project and shown to the editor, so they
// stay exactly as before; `effectLabel` gives the sentence-case label.
const IMAGE_EFFECTS = [
  'Fade In',
  'Fade Out',
  'Zoom In',
  'Zoom Out',
  'Slide In (Left)',
  'Slide In (Right)',
  'Slide In (Top)',
  'Slide In (Bottom)',
  'Rotate',
  'Blur',
  'Brightness',
  'Contrast',
  'Saturation',
  'Sepia',
  'Black & White',
  'Vignette',
  'Drop Shadow',
  'Glow',
  'Color Overlay',
  'Custom'
];

const AUDIO_EFFECTS = [
  'Fade In',
  'Fade Out',
  'Volume Boost',
  'Volume Reduce',
  'Echo',
  'Reverb',
  'Bass Boost',
  'Treble Boost',
  'Normalize',
  'Compress',
  'Gate',
  'Distortion',
  'Chorus',
  'Flanger',
  'Phaser',
  'EQ (Equalizer)',
  'Noise Reduction',
  'Speed Up',
  'Slow Down',
  'Custom'
];

const EFFECT_LABELS: Record<string, string> = {
  'Black & White': 'Black and white',
  'Color Overlay': 'Colour overlay',
  'EQ (Equalizer)': 'EQ (equalizer)',
};

/** "Slide In (Left)" -> "Slide in (left)". */
export const effectLabel = (value: string): string =>
  EFFECT_LABELS[value] ?? (value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value);

const { intensityRange, durationRange } = ASSET_LIMITS.effects;

/**
 * Effects the creator wants on one image or audio file, as a disclosure inside
 * the file's row: a quiet toggle with the count, then an add row and the list.
 */
const AssetEffectsManager: React.FC<AssetEffectsManagerProps> = ({
  assetId,
  assetType,
  assetName,
  effects,
  onEffectsChange
}) => {
  const regionId = `${useId()}-effects-${assetId}`;
  const [expanded, setExpanded] = useState(false);
  const [newEffect, setNewEffect] = useState<Partial<AssetEffect>>({
    type: '',
    intensity: 50,
    duration: 1,
    notes: ''
  });

  const availableEffects = assetType === 'image' ? IMAGE_EFFECTS : AUDIO_EFFECTS;
  const maxEffects = ASSET_LIMITS.effects.maxPerAsset;

  const handleAddEffect = () => {
    if (!newEffect.type) return;

    const effect: AssetEffect = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      type: newEffect.type,
      intensity: newEffect.intensity || 50,
      duration: newEffect.duration || 1,
      notes: newEffect.notes || ''
    };

    onEffectsChange([...effects, effect]);
    setNewEffect({ type: '', intensity: 50, duration: 1, notes: '' });
  };

  const handleRemoveEffect = (id: string) => {
    onEffectsChange(effects.filter(e => e.id !== id));
  };

  const handleUpdateEffect = (id: string, updates: Partial<AssetEffect>) => {
    onEffectsChange(
      effects.map(e => e.id === id ? { ...e, ...updates } : e)
    );
  };

  const canAddMore = effects.length < maxEffects;
  const rowGrid = {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      sm: 'minmax(0, 1.4fr) 104px 104px minmax(0, 2fr) auto',
    },
    gap: 1.5,
    alignItems: 'center',
  } as const;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Button
        size="small"
        variant="text"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={regionId}
        endIcon={
          <ExpandMoreOutlinedIcon
            sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
          />
        }
        sx={{ px: 1, ml: -1, color: 'text.secondary', fontWeight: 600 }}
      >
        {effects.length > 0 ? `Effects (${effects.length})` : 'Add effects'}
      </Button>

      <Collapse in={expanded}>
        <Box id={regionId} role="region" aria-label={`Effects for ${assetName}`} sx={{ pt: 1.5 }}>
          {effects.length > 0 && (
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}>
              {effects.map((effect) => (
                <Box
                  component="li"
                  key={effect.id}
                  sx={{ ...rowGrid, py: 1.25, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0, pt: 0 } }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                    {effectLabel(effect.type)}
                  </Typography>
                  <TextField
                    size="small"
                    label="Intensity %"
                    type="number"
                    value={effect.intensity || 50}
                    onChange={(e) => handleUpdateEffect(effect.id, { intensity: parseInt(e.target.value) || 50 })}
                    inputProps={{ min: intensityRange.min, max: intensityRange.max, step: intensityRange.step }}
                  />
                  <TextField
                    size="small"
                    label="Duration (s)"
                    type="number"
                    value={effect.duration || 1}
                    onChange={(e) => handleUpdateEffect(effect.id, { duration: parseFloat(e.target.value) || 1 })}
                    inputProps={{ min: durationRange.min, max: durationRange.max, step: durationRange.step }}
                  />
                  <TextField
                    size="small"
                    label="Notes"
                    value={effect.notes || ''}
                    onChange={(e) => handleUpdateEffect(effect.id, { notes: e.target.value })}
                    placeholder="Anything specific"
                    inputProps={{ maxLength: ASSET_LIMITS.notes.maxLength }}
                    sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveEffect(effect.id)}
                    aria-label={`Remove ${effectLabel(effect.type).toLowerCase()} effect`}
                    sx={{ justifySelf: { xs: 'start', sm: 'center' } }}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {canAddMore ? (
            <Box sx={rowGrid}>
              <TextField
                select
                size="small"
                label="Effect"
                value={newEffect.type || ''}
                onChange={(e) => setNewEffect(prev => ({ ...prev, type: e.target.value }))}
                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
              >
                {availableEffects.map((effect) => (
                  <MenuItem key={effect} value={effect}>{effectLabel(effect)}</MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Intensity %"
                type="number"
                value={newEffect.intensity || 50}
                onChange={(e) => setNewEffect(prev => ({ ...prev, intensity: parseInt(e.target.value) || 50 }))}
                inputProps={{ min: intensityRange.min, max: intensityRange.max, step: intensityRange.step }}
              />
              <TextField
                size="small"
                label="Duration (s)"
                type="number"
                value={newEffect.duration || 1}
                onChange={(e) => setNewEffect(prev => ({ ...prev, duration: parseFloat(e.target.value) || 1 }))}
                inputProps={{ min: durationRange.min, max: durationRange.max, step: durationRange.step }}
              />
              <TextField
                size="small"
                label="Notes (optional)"
                value={newEffect.notes || ''}
                onChange={(e) => setNewEffect(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g. Slow fade"
                inputProps={{ maxLength: ASSET_LIMITS.notes.maxLength }}
                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddOutlinedIcon />}
                onClick={handleAddEffect}
                disabled={!newEffect.type}
                sx={{ justifySelf: 'start', whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You've added the most effects one file can have ({pluralize(maxEffects, 'effect')}).
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AssetEffectsManager;
